import React, { useState, useEffect, useCallback } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { omit } from 'lodash'
import * as api from '@/apis/aipass'
import { simpleTableStyle } from '@/constants/layout'
import { SettingSalesManagerTab } from '@/components/organisms/settings/sales-manager/tab'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { EditFooter } from '@/components/organisms/edit-footer'
import { SalesSubjectMasterType, SalesSubjectSetType } from '@/models/sales-manager/sales'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { SettingSalesSubjectEditButtons } from '@/components/organisms/settings/sales-manager/subject-set/edit-buttons'
import { SubjectSetModal } from '@/components/organisms/settings/sales-manager/subject-set/subject-set-modal'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'

export const SettingSalesManagerSubjectSet: React.FC = () => {
  const { errorHandler } = useErrorHandler()
  const { t } = useTranslation()
  const csvDataHeader = ['科目セットID', '科目セット名']
  const [salesSubjectSet, setSalesSubjectSet] = useState<SalesSubjectSetType[]>([])
  const [salesSubjectMasters, setSalesSubjectMasters] = useState<SalesSubjectMasterType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showSubjectSetModal, setShowSubjectSetModal] = useState<boolean>(false)
  const [editSubjectSet, setEditSubjectSet] = useState<SalesSubjectSetType>()
  const [deleteSalesSubjectSetIds, setDeleteSalesSubjectSetIds] = useState<string[]>([])
  const [csvData, setCsvData] = useState([csvDataHeader])
  const [nextDisplayOrder, setNextDisplayOrder] = useState<number>(1)
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()

  const fetchSalesSubjectSet = async () => {
    setIsLoading(true)
    const fetchedSalesSubjectSet = await api.fetchSalesSubjectSet()
    setSalesSubjectSet(fetchedSalesSubjectSet)
    setNextDisplayOrder(Number(fetchedSalesSubjectSet.slice(-1)[0]?.salesSubjectSetNameId || 0) + 1)
    setIsLoading(false)
  }

  const fetchSalesSubject = async () => {
    try {
      setIsLoading(true)
      const res = await api.fetchSalesSubjectMaster()
      setSalesSubjectMasters(res)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const deleteSalesSubject = async () => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      await api.deleteSalesSubjectSet({ deleteSalesSubjectSetIds })
      await fetchSalesSubjectSet()
      setDeleteSalesSubjectSetIds([])
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onFileLoad = async data => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      if (!data?.[0]['科目セットID']) {
        return alert(t('Subject set ID (order) is empty'))
      }
      if (!data?.[0]['科目セット名']) {
        return alert(t('Subject set name is empty'))
      }
      const salesSubjectSetList = data.map(item => ({
        salesSubjectSetNameId: item['科目セットID'] || '',
        salesSubjectSetName: item['科目セット名'] || '',
        salesSubjectList: Object.values(omit(item, '科目セットID', '科目セット名')).filter(i => !!i),
      }))
      const res = await api.updateSalesSubjectSetList({ salesSubjectSetList })
      await fetchSalesSubjectSet()
      if (res?.errors) errorHandler({ response: { data: res } })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onClickAdd = () => {
    if (!guardSettingsEdit()) return
    if (showSubjectSetModal) return
    setEditSubjectSet(undefined)
    setShowSubjectSetModal(true)
  }

  const onClickEdit = item => {
    if (!guardSettingsEdit()) return
    // モーダル表示中の背面クリックで editSubjectSet が差し替わり、科目選択が初期化されるのを防ぐ
    if (showSubjectSetModal) return
    setEditSubjectSet(item)
    setShowSubjectSetModal(true)
  }

  const onCancelEdit = () => {
    setEditSubjectSet(undefined)
    setShowSubjectSetModal(false)
  }

  const onClickDelete = item => {
    if (!guardSettingsEdit()) return
    setDeleteSalesSubjectSetIds([...deleteSalesSubjectSetIds, item.salesSubjectSetId])
  }

  const onSuccessChanged = () => {
    onCancelEdit()
    fetchSalesSubjectSet()
  }

  useEffect(() => {
    fetchSalesSubjectSet()
    fetchSalesSubject()
  }, [])

  useEffect(() => {
    const maxSubjectCount =
      salesSubjectSet?.reduce((max, i) => (max > i.salesSubjectSetMap.length ? max : i.salesSubjectSetMap.length), 0) || 0

    setCsvData([
      [...csvDataHeader, ...[...Array(maxSubjectCount)].map((i, index) => `科目(${index + 1})`)],
      ...salesSubjectSet.map(item => [
        String(item.salesSubjectSetNameId),
        item.salesSubjectSetName,
        ...item.salesSubjectSetMap.map(i => i.salesSubjectMasterName),
      ]),
    ])
  }, [salesSubjectSet])

  const footerContent = deleteSalesSubjectSetIds?.length ? (
    <EditFooter onSave={deleteSalesSubject} onCancel={() => setDeleteSalesSubjectSetIds([])} />
  ) : undefined

  return (
    <>
      {showSubjectSetModal && (
        <SubjectSetModal
          nextDisplayOrder={nextDisplayOrder || 1}
          salesSubjectMasters={salesSubjectMasters}
          editSubjectSet={editSubjectSet}
          onClickCancel={onCancelEdit}
          onSuccess={onSuccessChanged}
        />
      )}
      <SettingsLayout loading={isLoading} footerContent={footerContent}>
        <TabContainer tabComponent={<SettingSalesManagerTab currentTab="Subject set" />} isShowFooter={!!deleteSalesSubjectSetIds?.length}>
          <SettingSalesSubjectEditButtons
            csvData={csvData}
            onFileLoad={onFileLoad}
            onClickAddButton={onClickAdd}
            canEdit={!isSettingsReadOnly}
          />

          <table css={salesSubjectSetTableStyle}>
            <thead>
              <tr>
                <th css={{ width: '16%' }}>{t('Subject set ID (order)')}</th>
                <th css={{ width: '29%' }}>{t('Subject set name')}</th>
                <th css={{ width: '55%' }}>{t('Subject')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {salesSubjectSet.map(item => {
                if (deleteSalesSubjectSetIds.includes(item.salesSubjectSetId)) return null

                return (
                  <tr key={item.salesSubjectSetId} css={{ cursor: 'pointer' }} onClick={() => onClickEdit(item)}>
                    <td css={{ paddingTop: 16, paddingBottom: 16 }}>{item.salesSubjectSetNameId}</td>
                    <td css={{ paddingTop: 16, paddingBottom: 16 }}>{item.salesSubjectSetName}</td>
                    <td css={{ paddingTop: 16, paddingBottom: 16 }}>
                      {item.salesSubjectSetMap
                        .map(i => [i.salesSubjectMasterName, i.salesSubSubjectName].filter(v => v).join('/'))
                        .join(', ')}
                    </td>
                    <td css={{ paddingTop: 16, paddingBottom: 16 }}>
                      <DeleteIcon onClick={() => onClickDelete(item)} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TabContainer>
      </SettingsLayout>
    </>
  )
}

const salesSubjectSetTableStyle = css(simpleTableStyle, {
  'tr th': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  overflow: 'auto',
  display: 'block',
})
